<script setup>
import { ref } from 'vue';
import { VueFlow, Position } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

// Define nodes for the LRS A2 workflow
const nodes = ref([
  // Step 1: Login
  {
    id: 'step1',
    type: 'default',
    position: { x: 25, y: 10 },
    data: { label: '🏦 ICICI BANK | 🔐 STEP 1: Login\n\niMobile Pay: Send Money > Transfer Overseas | Net Banking: Payments > Outward Remittance' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #ff6600 0%, #e65c00 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11px',
      padding: '12px',
      borderRadius: '10px',
      width: '280px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(255, 102, 0, 0.3)',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.4',
      border: '2px solid #ff8533'
    }
  },

  // Step 2: Add Beneficiary
  {
    id: 'step2',
    type: 'default',
    position: { x: 380, y: 10 },
    data: { label: '➕ STEP 2: Add Beneficiary\n\n🏦 "Add New Receiver" | 📍 Destination: India (GIFT) / USA (IBKR) | 👤 Category: My Bank Account' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      border: '2px solid #0ea5e9',
      borderRadius: '10px',
      padding: '12px',
      width: '280px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#0c4a6e',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(14, 165, 233, 0.2)',
      lineHeight: '1.4'
    }
  },

  // Step 2.1: Bank Details
  {
    id: 'step2-1',
    type: 'default',
    position: { x: 735, y: 10 },
    data: { label: '🏛️ Enter Bank Details\n\n📝 SWIFT Code | 💳 Account Number | 🏢 Branch Details\n\n⚠️ See guide below for exact details' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
      border: '2px solid #3b82f6',
      borderRadius: '10px',
      padding: '12px',
      width: '260px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#1e40af',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(59, 130, 246, 0.2)',
      lineHeight: '1.4'
    }
  },

  // Step 3: Purpose Code
  {
    id: 'step3',
    type: 'default',
    position: { x: 400, y: 135 },
    data: { label: '🎯 STEP 3: Select Purpose Code | ⚠️ Critical for RBI Compliance' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '12px',
      padding: '12px 16px',
      borderRadius: '10px',
      width: '280px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(245, 158, 11, 0.35)',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.4'
    }
  },

  // Step 3.1: GIFT City Route
  {
    id: 'step3-gift',
    type: 'default',
    position: { x: 25, y: 200 },
    data: { label: '🏙️ GIFT City Route\n\n📋 Purpose: Own Account Transfer | 🔢 Code: S0023\n"Transfer to own account overseas"' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Right,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '2px solid #f59e0b',
      borderRadius: '10px',
      padding: '12px',
      width: '290px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#92400e',
      fontWeight: '600',
      boxShadow: '0 3px 12px rgba(245, 158, 11, 0.2)',
      lineHeight: '1.4'
    }
  },

  // Step 3.2: IBKR Direct Route
  {
    id: 'step3-ibkr',
    type: 'default',
    position: { x: 705, y: 200 },
    data: { label: '🇺🇸 Direct 🇺🇸 (IBKR)\n\n📋 Purpose: Equity Investment | 🔢 Code: S0001\n"Indian investment abroad - equity"' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '2px solid #ea580c',
      borderRadius: '10px',
      padding: '12px',
      width: '290px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '10.5px',
      color: '#9a3412',
      fontWeight: '600',
      boxShadow: '0 3px 12px rgba(234, 88, 12, 0.2)',
      lineHeight: '1.4'
    }
  },

  // Step 4: Fees Calculator
  {
    id: 'step4',
    type: 'default',
    position: { x: 25, y: 325 },
    data: { label: '💰 STEP 4: Review Fees & TCS | 🧮 The Calculator' },
    sourcePosition: Position.Right,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '12px',
      padding: '12px 16px',
      borderRadius: '10px',
      width: '260px',
      textAlign: 'center',
      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.35)',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.4'
    }
  },

  // Step 4.1: Exchange Rate
  {
    id: 'step4-rate',
    type: 'default',
    position: { x: 315, y: 325 },
    data: { label: '💱 Exchange Rate | Live INR/USD rate' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '10px',
      padding: '12px',
      width: '220px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '11px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.15)',
      lineHeight: '1.4'
    }
  },

  // Step 4.2: GST
  {
    id: 'step4-gst',
    type: 'default',
    position: { x: 565, y: 325 },
    data: { label: '📊 GST on Conversion | Standard tiered GST' },
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #059669',
      borderRadius: '10px',
      padding: '12px',
      width: '220px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '11px',
      color: '#065f46',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(5, 150, 105, 0.15)',
      lineHeight: '1.4'
    }
  },

  // Step 4.3: Remittance Fee
  {
    id: 'step4-fee',
    type: 'default',
    position: { x: 815, y: 325 },
    data: { label: '💳 Remittance Fee | ₹750 - ₹1,000' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Left,
    style: {
      background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
      border: '2px solid #10b981',
      borderRadius: '10px',
      padding: '12px',
      width: '200px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '11px',
      color: '#064e3b',
      fontWeight: '500',
      boxShadow: '0 3px 12px rgba(16, 185, 129, 0.15)',
      lineHeight: '1.4'
    }
  },

  // Step 4.4: TCS
  {
    id: 'step4-tcs',
    type: 'default',
    position: { x: 330, y: 420 },
    data: { label: '⚠️ TCS (Tax Collected at Source) | 20% on amount > ₹10 Lakh/year\n💡 Refundable when filing ITR' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '3px solid #dc2626',
      borderRadius: '10px',
      padding: '12px',
      width: '380px',
      whiteSpace: 'pre-wrap',
      textAlign: 'center',
      fontSize: '11.5px',
      color: '#991b1b',
      fontWeight: 'bold',
      boxShadow: '0 4px 16px rgba(220, 38, 38, 0.25)',
      lineHeight: '1.4'
    }
  },

  // Step 5: E-Sign & Confirm
  {
    id: 'step5',
    type: 'default',
    position: { x: 290, y: 515 },
    data: { label: '✍️ STEP 5: E-Sign & Confirm\n\n✅ Accept LRS Declaration (Digital Form A2) | 🔐 Authenticate: Debit Card Grid / OTP | 🎉 Transfer Complete!' },
    sourcePosition: Position.Bottom,
    targetPosition: Position.Top,
    style: {
      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '11.5px',
      padding: '14px',
      borderRadius: '10px',
      width: '460px',
      textAlign: 'center',
      boxShadow: '0 6px 20px rgba(139, 92, 246, 0.35)',
      whiteSpace: 'pre-wrap',
      lineHeight: '1.5'
    }
  }
]);

// Define edges with proper directional flow
const edges = ref([
  // Step 1 to Step 2
  {
    id: 'e1-2',
    source: 'step1',
    target: 'step2',
    animated: true,
    style: { stroke: '#3b82f6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#3b82f6', width: 20, height: 20 }
  },
  // Step 2 to Step 2.1
  {
    id: 'e2-2.1',
    source: 'step2',
    target: 'step2-1',
    animated: true,
    style: { stroke: '#0ea5e9', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#0ea5e9', width: 20, height: 20 }
  },
  // Step 2.1 to Step 3
  {
    id: 'e2.1-3',
    source: 'step2-1',
    target: 'step3',
    animated: true,
    style: { stroke: '#f59e0b', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#f59e0b', width: 20, height: 20 }
  },
  // Step 3 to Step 3 GIFT (Left arrow)
  {
    id: 'e3-3gift',
    source: 'step3',
    target: 'step3-gift',
    animated: true,
    style: { stroke: '#f59e0b', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#f59e0b', width: 20, height: 20 }
  },
  // Step 3 to Step 3 IBKR (Right arrow)
  {
    id: 'e3-3ibkr',
    source: 'step3',
    target: 'step3-ibkr',
    animated: true,
    style: { stroke: '#ea580c', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#ea580c', width: 20, height: 20 }
  },
  // Step 3 GIFT to Step 4
  {
    id: 'e3gift-4',
    source: 'step3-gift',
    target: 'step4',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  // Step 3 IBKR to Step 4
  {
    id: 'e3ibkr-4',
    source: 'step3-ibkr',
    target: 'step4',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  // Step 4 to components
  {
    id: 'e4-rate',
    source: 'step4',
    target: 'step4-rate',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  {
    id: 'erate-gst',
    source: 'step4-rate',
    target: 'step4-gst',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  {
    id: 'egst-fee',
    source: 'step4-gst',
    target: 'step4-fee',
    animated: true,
    style: { stroke: '#10b981', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#10b981', width: 20, height: 20 }
  },
  {
    id: 'efee-tcs',
    source: 'step4-fee',
    target: 'step4-tcs',
    animated: true,
    style: { stroke: '#dc2626', strokeWidth: 4 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#dc2626', width: 22, height: 22 }
  },
  // Step 4 TCS to Step 5
  {
    id: 'e4tcs-5',
    source: 'step4-tcs',
    target: 'step5',
    animated: true,
    style: { stroke: '#8b5cf6', strokeWidth: 3 },
    type: 'smoothstep',
    markerEnd: { type: 'arrowclosed', color: '#8b5cf6', width: 20, height: 20 }
  }
]);
</script>

<template>
  <div class="lrs-workflow">
    <h2 class="workflow-title">🏦 LRS A2 Fund Transfer Process - ICICI Bank</h2>
    <p class="workflow-intro">
      <strong>Digital Form A2 Execution:</strong> Step-by-step guide to transfer funds overseas via Liberalized Remittance Scheme (LRS) using ICICI Bank.
    </p>

    <!-- Interactive Workflow Diagram -->
    <div class="workflow-container" style="height: 640px; border: 2px solid #e5e7eb; border-radius: 16px; background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);">
      <VueFlow
        :nodes="nodes"
        :edges="edges"
        :fit-view-on-init="false"
        :min-zoom="0.6"
        :max-zoom="1.5"
        :default-viewport="{ zoom: 1, x: 0, y: 0 }"
      >
        <Background pattern-color="#d1d5db" :gap="20" size="1" />
        <Controls />
      </VueFlow>
    </div>

    <div class="workflow-info">
      <i class="pi pi-info-circle"></i>
      <p><strong>Interactive Guide:</strong> Use mouse wheel to zoom, drag to pan. Each step shows the exact process for LRS Form A2 execution.</p>
    </div>

    <!-- Key Information Cards -->
    <div class="key-info-section">
      <h3>📋 Important Information</h3>
      <div class="info-grid">
        <div class="info-card purpose">
          <i class="pi pi-bookmark"></i>
          <h4>Purpose Codes</h4>
          <ul>
            <li><strong>S0023:</strong> GIFT City - Own Account</li>
            <li><strong>S0001:</strong> IBKR - Equity Investment</li>
          </ul>
        </div>
        <div class="info-card limits">
          <i class="pi pi-chart-bar"></i>
          <h4>LRS Limits</h4>
          <ul>
            <li><strong>Annual Limit:</strong> $250,000</li>
            <li><strong>TCS Threshold:</strong> ₹10 Lakh</li>
            <li><strong>TCS Rate:</strong> 20% (refundable)</li>
          </ul>
        </div>
        <div class="info-card fees">
          <i class="pi pi-money-bill"></i>
          <h4>Typical Fees</h4>
          <ul>
            <li><strong>Remittance Fee:</strong> ₹750-₹1,000</li>
            <li><strong>GST:</strong> Tiered (18%)</li>
            <li><strong>Forex Markup:</strong> ~1-2%</li>
          </ul>
        </div>
        <div class="info-card timeline">
          <i class="pi pi-clock"></i>
          <h4>Processing Time</h4>
          <ul>
            <li><strong>GIFT City:</strong> 1-2 business days</li>
            <li><strong>Direct 🇺🇸:</strong> 3-5 business days</li>
            <li><strong>E-Sign:</strong> Instant</li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Bank Details Guide -->
    <div class="bank-details-guide">
      <h3>🏦 Bank Details Entry Guide (Step 2)</h3>
      <p class="guide-intro">⚠️ <strong>Important:</strong> Enter these details exactly as provided. Use your route-specific information.</p>

      <div class="route-cards">
        <!-- Route 1: GIFT City -->
        <div class="route-card gift-route">
          <div class="route-header">
            <span class="route-badge">Route 1</span>
            <h4>🏙️ GIFT City (ICICI IBU-FCA)</h4>
          </div>
          <div class="route-details">
            <div class="detail-item">
              <span class="detail-label">👤 Beneficiary Name:</span>
              <span class="detail-value">Your Legal Name (as on GIFT City account)</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">💳 Account Number:</span>
              <span class="detail-value">12-15 digit USD Account from ICICI IBU<br/><small>⚠️ Different from your INR account</small></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">🔢 SWIFT Code:</span>
              <span class="detail-value"><code>ICICINBBXXX</code> or <code>ICICINBBIBU</code><br/><small>Last 3 chars = IBU branch identifier</small></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">🏢 Bank & Branch:</span>
              <span class="detail-value">ICICI Bank Ltd, GIFT City Branch<br/>Gandhinagar, Gujarat</span>
            </div>
          </div>
        </div>

        <!-- Route 2: IBKR Direct -->
        <div class="route-card ibkr-route">
          <div class="route-header">
            <span class="route-badge">Route 2</span>
            <h4>🇺🇸 Direct 🇺🇸 (IBKR)</h4>
          </div>
          <div class="route-details">
            <div class="detail-item">
              <span class="detail-label">👤 Beneficiary Name:</span>
              <span class="detail-value">Interactive Brokers LLC<br/><small>Or: "Your Name - U1234567"</small></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">💳 Account Number:</span>
              <span class="detail-value">Virtual IBAN from ICICI Direct Global<br/><small>💡 Usually starts with IN... or unique digits</small></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">🔢 SWIFT Code:</span>
              <span class="detail-value"><code>CITIUS33</code> (Citibank NY)<br/><code>CHASUS33</code> (JP Morgan NY)</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">🏢 Bank Address:</span>
              <span class="detail-value">One Pickwick Plaza<br/>Greenwich, CT 06830, USA</span>
            </div>
          </div>
        </div>
      </div>

      <div class="guide-tip">
        <i class="pi pi-exclamation-triangle"></i>
        <p><strong>Critical:</strong> Double-check all details before submission. Incorrect details may cause delays or failed transfers. Refer to your account opening documents or contact your bank for exact details.</p>
      </div>
    </div>

    <!-- Pro Tips -->
    <div class="pro-tips">
      <h3>💡 Pro Tips</h3>
      <div class="tips-grid">
        <div class="tip-card">
          <span class="tip-icon">💰</span>
          <p><strong>Save TCS:</strong> Transfer in smaller batches below ₹10L annually to avoid 20% TCS upfront.</p>
        </div>
        <div class="tip-card">
          <span class="tip-icon">📱</span>
          <p><strong>Use iMobile:</strong> Faster processing and better rates than branch visits.</p>
        </div>
        <div class="tip-card">
          <span class="tip-icon">📊</span>
          <p><strong>Track Forex:</strong> Time your transfers when USD is weaker for better conversion rates.</p>
        </div>
        <div class="tip-card">
          <span class="tip-icon">📝</span>
          <p><strong>Document Everything:</strong> Keep all transaction records for ITR filing and TCS refund claims.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.lrs-workflow {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 20px;
}

.workflow-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.workflow-intro {
  font-size: 1rem;
  color: #6b7280;
  margin-bottom: 32px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.15);
  line-height: 1.6;
}

.workflow-intro strong {
  color: #92400e;
  font-weight: 700;
}

.workflow-info {
  margin-top: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-left: 4px solid #3b82f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  color: #1e40af;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
}

.workflow-info i {
  color: #3b82f6;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.workflow-info p {
  margin: 0;
  line-height: 1.6;
}

.key-info-section {
  margin-top: 40px;
  padding: 24px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 12px;
  border: 2px solid #bae6fd;
}

.key-info-section h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 24px 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.info-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  border: 2px solid #bae6fd;
  transition: all 0.3s ease;
}

.info-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.info-card i {
  font-size: 2rem;
  margin-bottom: 12px;
}

.info-card.purpose i { color: #f59e0b; }
.info-card.limits i { color: #dc2626; }
.info-card.fees i { color: #10b981; }
.info-card.timeline i { color: #8b5cf6; }

.info-card h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.info-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-card ul li {
  padding: 6px 0;
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.5;
}

.info-card ul li strong {
  color: #1f2937;
  font-weight: 600;
}

.pro-tips {
  margin-top: 40px;
  padding: 24px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 12px;
  border: 2px solid #fbbf24;
}

.pro-tips h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #78350f;
  margin: 0 0 20px 0;
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.tip-card {
  background: white;
  padding: 18px;
  border-radius: 10px;
  border: 2px solid #fbbf24;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  transition: all 0.3s ease;
}

.tip-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(245, 158, 11, 0.2);
  border-color: #f59e0b;
}

.tip-icon {
  font-size: 1.8rem;
  flex-shrink: 0;
}

.tip-card p {
  margin: 0;
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.5;
}

.tip-card strong {
  color: #92400e;
  font-weight: 600;
}

.bank-details-guide {
  margin-top: 40px;
  padding: 28px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 12px;
  border: 2px solid #86efac;
}

.bank-details-guide h3 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #14532d;
  margin: 0 0 12px 0;
}

.guide-intro {
  font-size: 1rem;
  color: #166534;
  margin: 0 0 24px 0;
  padding: 12px 16px;
  background: white;
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  line-height: 1.6;
}

.route-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.route-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.route-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.route-header {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.gift-route .route-header {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
}

.ibkr-route .route-header {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.route-badge {
  background: rgba(0, 0, 0, 0.1);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.gift-route .route-badge {
  color: #92400e;
}

.ibkr-route .route-badge {
  color: #1e40af;
}

.route-header h4 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
}

.gift-route .route-header h4 {
  color: #92400e;
}

.ibkr-route .route-header h4 {
  color: #1e40af;
}

.route-details {
  padding: 20px;
}

.detail-item {
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  font-weight: 700;
  color: #374151;
  font-size: 0.9rem;
}

.detail-value {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.6;
}

.detail-value code {
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  color: #1f2937;
  font-weight: 600;
  font-size: 0.85rem;
}

.detail-value small {
  display: block;
  color: #9ca3af;
  font-size: 0.8rem;
  margin-top: 4px;
}

.guide-tip {
  padding: 16px 20px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #fca5a5;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.guide-tip i {
  color: #dc2626;
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.guide-tip p {
  margin: 0;
  color: #991b1b;
  font-size: 0.95rem;
  line-height: 1.6;
}

.guide-tip strong {
  color: #7f1d1d;
  font-weight: 700;
}

@media (max-width: 1024px) {
  .route-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .workflow-title {
    font-size: 1.5rem;
  }

  .workflow-container {
    height: 900px !important;
  }

  .info-grid,
  .tips-grid {
    grid-template-columns: 1fr;
  }

  .bank-details-guide {
    padding: 20px 16px;
  }

  .bank-details-guide h3 {
    font-size: 1.3rem;
  }

  .route-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1200px) {
  .workflow-container {
    height: 750px !important;
  }
}
</style>
